import { gtm } from '../modules/gtm.js';

function debounce( fn, ms ) {
	let t;
	return function ( ...args ) { clearTimeout( t ); t = setTimeout( () => fn.apply( this, args ), ms ); };
}

export const filterMethods = {

	initFilters() {
		this._setupDualRange( 'duration', { step: 0.5 } );
		this._setupDualRange( 'distance', { step: 1 } );
		this._setupDualRange( 'climb',    { step: 50 } );
		this._setupDifficultySlider();
		this._bindBikeTypeCheckboxes();
	},

	_setupDualRange( name, { step = 1 } = {} ) {
		const minEl  = document.getElementById( `filter-${ name }-min` );
		const maxEl  = document.getElementById( `filter-${ name }-max` );
		const minLbl = document.getElementById( `label-${ name }-min` );
		const maxLbl = document.getElementById( `label-${ name }-max` );
		if ( ! minEl || ! maxEl ) return;

		const dataMin = parseFloat( minEl.min );
		const dataMax = parseFloat( maxEl.max );

		this.filters[ `${ name }Min` ] = dataMin;
		this.filters[ `${ name }Max` ] = dataMax;

		const sync = () => {
			let lo = parseFloat( minEl.value );
			let hi = parseFloat( maxEl.value );
			if ( lo > hi ) { lo = hi; minEl.value = lo; }
			if ( hi < lo ) { hi = lo; maxEl.value = hi; }
			if ( minLbl ) minLbl.textContent = lo;
			if ( maxLbl ) maxLbl.textContent = hi;
			this.filters[ `${ name }Min` ] = lo;
			this.filters[ `${ name }Max` ] = hi;
			this._applyFilters();
		};

		const updateFill = () => {
			const lo  = parseFloat( minEl.value );
			const hi  = parseFloat( maxEl.value );
			const pct = ( v ) => ( ( v - dataMin ) / ( dataMax - dataMin ) ) * 100;
			minEl.closest( '.range-dual' ).style.setProperty( '--range-lo', `${ pct( lo ) }%` );
			minEl.closest( '.range-dual' ).style.setProperty( '--range-hi', `${ pct( hi ) }%` );
		};

		const fireGtm = debounce( () => {
			gtm.mapFilterApply( {
				filter_type  : name,
				filter_value : `${ minEl.value }-${ maxEl.value }`,
			} );
		}, 600 );

		minEl.addEventListener( 'input', () => { sync(); updateFill(); fireGtm(); } );
		maxEl.addEventListener( 'input', () => { sync(); updateFill(); fireGtm(); } );
		updateFill();
	},

	_setupDifficultySlider() {
		const input = document.getElementById( 'filter-difficulty' );
		const label = document.getElementById( 'label-difficulty' );
		const dots  = document.querySelectorAll( '#difficulty-dots-preview .dot' );
		if ( ! input ) return;

		const render = ( val ) => {
			const s = window.lbhMapStrings || {};
			if ( label ) label.textContent = val === 0 ? ( s.filterAllDifficulty || 'Tutte' ) : `${ val } / 5`;
			dots.forEach( ( dot, i ) => dot.classList.toggle( 'is-filled', i < val ) );
		};

		render( 0 );

		const fireGtmDiff = debounce( ( val ) => {
			gtm.mapFilterApply( { filter_type: 'difficulty', filter_value: val } );
		}, 600 );

		input.addEventListener( 'input', () => {
			const val = parseInt( input.value, 10 );
			render( val );
			this.filters.difficulty = val;
			this._applyFilters();
			fireGtmDiff( val );
		} );
	},

	_bindBikeTypeCheckboxes() {
		document.querySelectorAll( '.filter-bike-type' ).forEach( cb => {
			cb.addEventListener( 'change', () => {
				this.filters.bikeTypes = Array.from(
					document.querySelectorAll( '.filter-bike-type:checked' )
				).map( el => el.value );
				this._applyFilters();
				gtm.mapFilterApply( {
					filter_type  : 'bike_type',
					filter_value : this.filters.bikeTypes.join( ',' ) || 'none',
				} );
			} );
		} );
	},

	initHotelFilters() {
		const promoEl  = document.getElementById( 'filter-has-promo' );
		const ratingEl = document.getElementById( 'filter-hotel-rating' );
		const dots     = document.querySelectorAll( '#hotel-rating-dots-preview .dot' );

		if ( promoEl ) {
			if ( this.initialHasPromo ) {
				promoEl.checked = true;
				this.hotelFilters.hasPromo = true;
			}

			promoEl.addEventListener( 'change', () => {
				this.hotelFilters.hasPromo = promoEl.checked;
				this._applyHotelFilters();
				gtm.mapFilterApply( { filter_type: 'hotel_promo', filter_value: promoEl.checked ? '1' : '0' } );
			} );
		}

		if ( ratingEl ) {
			const labelEl      = document.getElementById( 'label-hotel-rating' );
			const fireGtmRating = debounce( ( val ) => {
				gtm.mapFilterApply( { filter_type: 'hotel_rating', filter_value: val } );
			}, 600 );

			ratingEl.addEventListener( 'input', () => {
				const val = parseInt( ratingEl.value, 10 );
				const s   = window.lbhMapStrings || {};
				this.hotelFilters.rating = val;
				if ( labelEl ) labelEl.textContent = val === 0 ? ( s.filterAllRating || 'Tutti' ) : `${ val } / 5`;
				dots.forEach( ( dot, i ) => dot.classList.toggle( 'is-filled', i < val ) );
				this._applyHotelFilters();
				fireGtmRating( val );
			} );
		}
	},

	_applyHotelFilters() {
		const { hasPromo, rating } = this.hotelFilters;

		this._allHotelMarkers.forEach(({ id, el: markerEl }) => {
			const hotel = this.hotels.find( h => h.id === id );
			if ( ! hotel ) return;
			const visible = ( ! hasPromo || hotel.has_promo ) && ( rating === 0 || hotel.rating >= rating );
			markerEl.style.display = visible ? '' : 'none';
		} );

		if ( this._renderHotelAllList ) this._renderHotelAllList(
			document.getElementById( 'hotel-search' )?.value || ''
		);
	},

	_applyFilters() {
		const { durationMin, durationMax, distanceMin, distanceMax, climbMin, climbMax, difficulty, bikeTypes } = this.filters;

		const visible = this.itineraries.filter( iti => {
			if ( iti.duration && ( iti.duration < durationMin || iti.duration > durationMax ) ) return false;
			if ( iti.distance && ( iti.distance < distanceMin || iti.distance > distanceMax ) ) return false;
			if ( iti.climb    && ( iti.climb    < climbMin    || iti.climb    > climbMax    ) ) return false;
			if ( difficulty > 0 && iti.difficulty !== difficulty ) return false;
			if ( bikeTypes.length > 0 && ! bikeTypes.some( bt => iti.bike_types.includes( bt ) ) ) return false;
			return true;
		} );

		this.map.getSource( 'itineraries' ).setData( this._buildGeoJSON( visible ) );
	},
};
